"use client";

import React, { useState, useMemo } from "react";
import { Check, Funnel as Filter, RotateCcw, Search, SlidersHorizontal, FlaskConical, ShieldCheck, ShoppingCart, ChevronRight, X } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface ProductItem {
  id: string;
  name: string;
  sequence: string;
  purity: string;
  cas: string;
  category: string;
  catalogNumber: string;
  priceInr: number;
  formattedPrice: string;
  badge: string;
  status: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
}

const PRODUCTS: ProductItem[] = [
  {
    id: "rp-bpc157",
    name: "BPC-157 Lyophilized Peptide",
    sequence: "Gly-Glu-Pro-Pro-Pro-Gly-Lys-Pro-Ala-Asp-Asp-Ala-Gly-Leu-Val",
    purity: "≥99.4% HPLC",
    cas: "137525-51-0",
    category: "Research peptides",
    catalogNumber: "RP-157-5MG",
    priceInr: 3200,
    formattedPrice: "₹3,200",
    badge: "LOT VERIFIED",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_bpc.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_bpc.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_bpc.png-wvc-sizes",
  },
  {
    id: "rp-tb500",
    name: "TB-500 (Thymosin Beta-4)",
    sequence: "Ac-Ser-Asp-Lys-Pro-Asp-Met-Ala-Glu-Ile-Glu-Lys-Phe-Asp...",
    purity: "≥99.1% HPLC",
    cas: "885340-08-9",
    category: "Research peptides",
    catalogNumber: "RP-500-10MG",
    priceInr: 4500,
    formattedPrice: "₹4,500",
    badge: "HPLC CERTIFIED",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tb.webp",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tb.webp-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tb.webp-wvc-sizes",
  },
  {
    id: "rp-ipamorelin",
    name: "Ipamorelin Acetate",
    sequence: "Aib-His-D-2-Nal-D-Phe-Lys-NH2",
    purity: "≥99.6% HPLC",
    cas: "170851-70-4",
    category: "Research peptides",
    catalogNumber: "RP-IPA-5MG",
    priceInr: 2900,
    formattedPrice: "₹2,900",
    badge: "MASS SPEC CHECKED",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_ipa.webp",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_ipa.webp-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_ipa.webp-wvc-sizes",
  },
  {
    id: "rp-cjc1295",
    name: "CJC-1295 Without DAC",
    sequence: "Tyr-D-Ala-Asp-Ala-Ile-Phe-Thr-Gln-Ser-Tyr-Arg-Lys-Val-Leu...",
    purity: "≥99.0% HPLC",
    cas: "863288-34-0",
    category: "Research peptides",
    catalogNumber: "RP-CJC-2MG",
    priceInr: 3400,
    formattedPrice: "₹3,400",
    badge: "LOT VERIFIED",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_cjc.webp",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_cjc.webp-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_cjc.webp-wvc-sizes",
  },
  {
    id: "rp-glow-blend",
    name: "Dual Protocol Regeneration Blend",
    sequence: "BPC-157 (5mg) + TB-500 (5mg) Dual Formulation",
    purity: "≥99.3% HPLC",
    cas: "Compound Blend",
    category: "Peptide blends",
    catalogNumber: "RP-BLD-01",
    priceInr: 7200,
    formattedPrice: "₹7,200",
    badge: "COMBINED ASSAY",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_blend_dual.webp",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_blend_dual.webp-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_blend_dual.webp-wvc-sizes",
  },
  {
    id: "rp-ghk-cu",
    name: "GHK-Cu Copper Tripeptide",
    sequence: "Gly-His-Lys:Cu2+",
    purity: "≥99.7% HPLC",
    cas: "49557-75-7",
    category: "Research peptides",
    catalogNumber: "RP-GHK-50MG",
    priceInr: 3900,
    formattedPrice: "₹3,900",
    badge: "ASSAY 99.7%",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_ghk.webp",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_ghk.webp-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_ghk.webp-wvc-sizes",
  },
  {
    id: "rp-semaglutide-std",
    name: "Analytical Peptide Reference Standard",
    sequence: "GLP-1 Receptor Agonist Reference Sequence",
    purity: "≥99.8% HPLC",
    cas: "910463-68-2",
    category: "Custom synthesis services",
    catalogNumber: "RP-STD-03",
    priceInr: 9800,
    formattedPrice: "₹9,800",
    badge: "PRIMARY STANDARD",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_standard_ref.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_standard_ref.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_standard_ref.png-wvc-sizes",
  },
  {
    id: "rp-tirz-ref",
    name: "Dual Incretin Assay Peptide",
    sequence: "39-Amino Acid Synthetic Polypeptide",
    purity: "≥99.5% HPLC",
    cas: "2023788-19-2",
    category: "Custom synthesis services",
    catalogNumber: "RP-DIA-5MG",
    priceInr: 11500,
    formattedPrice: "₹11,500",
    badge: "SPECTROMETRY VERIFIED",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tirz.webp",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tirz.webp-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tirz.webp-wvc-sizes",
  },
  {
    id: "rp-motsc",
    name: "MOTS-c Mitochondrial Derived Peptide",
    sequence: "Met-Arg-Trp-Gln-Glu-Met-Gly-Tyr-Ile-Phe-Tyr-Pro-Arg-Lys-Leu-Arg",
    purity: "≥99.0% HPLC",
    cas: "1627580-64-6",
    category: "Research peptides",
    catalogNumber: "RP-MOT-10MG",
    priceInr: 5100,
    formattedPrice: "₹5,100",
    badge: "HPLC VERIFIED",
    status: "In stock",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_mot.webp",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_mot.webp-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_mot.webp-wvc-sizes",
  }
];

const CATEGORIES = [
  "All products",
  "Research peptides",
  "Peptide blends",
  "Custom synthesis services"
];

const PURITY_OPTIONS = [
  "All purity tiers",
  "≥99.5% Ultra-high",
  "≥99.0% Analytical grade"
];

export default function Listings() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All products");
  const [selectedPurity, setSelectedPurity] = useState("All purity tiers");
  const [sortBy, setSortBy] = useState("featured");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  const handleAddToCart = () => {
    setCartCount((prev) => prev + 1);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("All products");
    setSelectedPurity("All purity tiers");
    setSortBy("featured");
  };

  const filteredProducts = useMemo(() => {
    return PRODUCTS.filter((item) => {
      const matchSearch =
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.sequence.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.cas.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.catalogNumber.toLowerCase().includes(searchQuery.toLowerCase());

      const matchCategory =
        selectedCategory === "All products" || item.category === selectedCategory;

      const matchPurity =
        selectedPurity === "All purity tiers" ||
        (selectedPurity === "≥99.5% Ultra-high" && parseFloat(item.purity.replace("≥", "").replace("% HPLC", "")) >= 99.5) ||
        (selectedPurity === "≥99.0% Analytical grade" && parseFloat(item.purity.replace("≥", "").replace("% HPLC", "")) >= 99.0);

      return matchSearch && matchCategory && matchPurity;
    }).sort((a, b) => {
      if (sortBy === "price-low") return a.priceInr - b.priceInr;
      if (sortBy === "price-high") return b.priceInr - a.priceInr;
      if (sortBy === "purity-high") return b.purity.localeCompare(a.purity);
      return 0;
    });
  }, [searchQuery, selectedCategory, selectedPurity, sortBy]);

  const activeFilterCount =
    (selectedCategory !== "All products" ? 1 : 0) +
    (selectedPurity !== "All purity tiers" ? 1 : 0) +
    (searchQuery.trim() !== "" ? 1 : 0);

  return (
    <section
      id="product-listings"
      data-nav="light"
      className="w-full bg-background text-foreground py-8 md:py-12 border-b border-border"
    >
      <div className="max-w-[1088px] mx-auto px-4 sm:px-6">
        {/* Compliance Header Banner */}
        <div className="mb-8 border border-border bg-muted/60 rounded-md p-3.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="size-4 text-primary shrink-0" />
            <p className="text-xs text-muted-foreground font-mono tracking-wide uppercase">
              RUO Protocol: All materials strictly designated for in-vitro laboratory research and analytical testing in India.
            </p>
          </div>
          <div className="flex items-center gap-3 self-end sm:self-center">
            <span className="text-xs text-foreground font-medium whitespace-nowrap">
              Inquiries: (+91) 1234-567890
            </span>
            <Link
              to="/ResearchInformationPage"
              className="text-xs font-semibold text-primary hover:underline whitespace-nowrap"
            >
              Assay standards
            </Link>
          </div>
        </div>

        {/* Top Control Bar */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 pb-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="md:hidden flex items-center gap-2 text-xs"
              onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
            >
              <SlidersHorizontal className="size-3.5" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-primary text-primary-foreground text-2xs px-1.5 py-0.2 rounded-full font-mono">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            <span className="text-xs text-muted-foreground font-mono">
              Displaying {filteredProducts.length} certified compound{filteredProducts.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search compound, CAS, or lot..."
                className="w-full bg-background border border-input text-foreground text-xs rounded-md pl-9 pr-3 py-2 outline-hidden focus:border-primary focus:ring-1 focus:ring-ring transition-colors placeholder:text-muted-foreground/60"
              />
              {searchQuery && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 h-auto w-auto p-0 text-muted-foreground hover:text-foreground hover:bg-transparent"
                >
                  <X className="size-3.5" />
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground hidden sm:inline-block">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-background border border-input text-foreground text-xs rounded-md px-3 py-2 outline-hidden focus:border-primary focus:ring-1 focus:ring-ring cursor-pointer"
              >
                <option value="featured">Lot verified</option>
                <option value="price-low">Price: low to high</option>
                <option value="price-high">Price: high to low</option>
                <option value="purity-high">HPLC purity (high)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Main Grid: Sidebar + Product Catalogue */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pt-8">
          {/* Sidebar Filter Panel */}
          <aside
            className={`md:col-span-3 flex-col gap-6 ${
              mobileFilterOpen ? "flex" : "hidden md:flex"
            }`}
          >
            <div className="border border-border rounded-md p-5 bg-card flex flex-col gap-6">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <div className="flex items-center gap-2">
                  <Filter className="size-4 text-primary" />
                  <span className="text-xs font-semibold tracking-wider uppercase text-foreground">
                    Filter by specification
                  </span>
                </div>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetFilters}
                    className="h-auto p-0 text-xs text-muted-foreground hover:text-primary"
                  >
                    <RotateCcw className="size-3 mr-1" />
                    Reset
                  </Button>
                )}
              </div>

              {/* Category Filter */}
              <div className="flex flex-col gap-2.5">
                <span className="text-xs font-semibold text-foreground tracking-wide uppercase">
                  Compound category
                </span>
                <div className="flex flex-col gap-1.5">
                  {CATEGORIES.map((cat, i) => {
                    const isSelected = selectedCategory === cat;
                    return (
                      <Button
                        key={cat}
                        data-index={i}
                        type="button"
                        variant="ghost"
                        onClick={() => setSelectedCategory(cat)}
                        className={`w-full h-auto justify-between text-left text-xs px-2.5 py-1.5 rounded-sm transition-colors ${
                          isSelected
                            ? "bg-primary text-primary-foreground font-medium hover:bg-primary hover:text-primary-foreground"
                            : "text-foreground hover:bg-muted font-normal"
                        }`}
                      >
                        <span>{cat}</span>
                        {isSelected && <Check className="size-3 text-primary-foreground" />}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Purity Filter */}
              <div className="flex flex-col gap-2.5 border-t border-border pt-4">
                <span className="text-xs font-semibold text-foreground tracking-wide uppercase">
                  Analytical purity tier
                </span>
                <div className="flex flex-col gap-1.5">
                  {PURITY_OPTIONS.map((tier, i) => {
                    const isSelected = selectedPurity === tier;
                    return (
                      <Button
                        key={tier}
                        data-index={i}
                        type="button"
                        variant="ghost"
                        onClick={() => setSelectedPurity(tier)}
                        className={`w-full h-auto justify-between text-left text-xs px-2.5 py-1.5 rounded-sm transition-colors ${
                          isSelected
                            ? "bg-primary text-primary-foreground font-medium hover:bg-primary hover:text-primary-foreground"
                            : "text-foreground hover:bg-muted font-normal"
                        }`}
                      >
                        <span className="font-mono">{tier}</span>
                        {isSelected && <Check className="size-3 text-primary-foreground" />}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Laboratory Information Block */}
              <div className="border border-border/80 bg-muted/40 rounded-sm p-3.5 flex flex-col gap-2">
                <div className="flex items-center gap-1.5 text-primary">
                  <FlaskConical className="size-3.5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Certificate of analysis
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Batch-specific HPLC chromatogram traces and mass spectrometry data sheets are included with each dispatch.
                </p>
                <Link
                  to="/ResearchInformationPage"
                  className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1 pt-1"
                >
                  Verify batch COA
                  <ChevronRight className="size-3" />
                </Link>
              </div>

              {/* Institutional Procurement Notice */}
              <div className="border-t border-border pt-4">
                <p className="text-2xs text-muted-foreground/80 leading-relaxed font-mono">
                  SUPPLIER CODE: RP-IN-2025
                  <br />
                  DOMESTIC DISPATCH: Cold-chain validated shipping across Indian research centers.
                </p>
              </div>
            </div>
          </aside>

          {/* Product Grid Area */}
          <div className="md:col-span-9 flex flex-col gap-6">
            {filteredProducts.length === 0 ? (
              <div className="border border-dashed border-border rounded-md p-12 text-center flex flex-col items-center gap-3">
                <FlaskConical className="size-8 text-muted-foreground" />
                <p className="text-base font-semibold text-foreground">
                  No research compounds match your selection
                </p>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Adjust your search terms or reset the purity and category filters to inspect all laboratory stock.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetFilters}
                  className="mt-2 text-xs"
                >
                  Reset catalogue filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredProducts.map((product, i) => (
                  <Card
                    key={product.id}
                    data-index={i}
                    className="border border-border rounded-md bg-card transition-[border-color,box-shadow] duration-200 hover:border-primary hover:shadow-md flex flex-col justify-between overflow-hidden"
                  >
                    <div>
                      {/* Image Frame with Specimen Backplate */}
                      <div className="relative h-52 bg-muted/50 border-b border-border flex items-center justify-center p-4">
                        <div className="absolute top-2.5 left-2.5">
                          <span className="inline-block bg-background/90 border border-primary/30 text-primary text-2xs font-mono font-semibold px-2 py-0.5 rounded-xs">
                            {product.badge}
                          </span>
                        </div>
                        <div className="absolute top-2.5 right-2.5">
                          <span className="text-2xs text-muted-foreground font-mono bg-background/80 px-1.5 py-0.5 rounded-xs border border-border">
                            {product.catalogNumber}
                          </span>
                        </div>
                        <img
                          src={product.imageSrc}
                          data-wvc-srcset={product.imageSrcset}
                          data-wvc-sizes={product.imageSizes}
                          alt={product.name}
                          loading="lazy"
                          decoding="async"
                          className="max-h-40 max-w-full object-contain filter contrast-105"
                        />
                      </div>

                      {/* Card Content & Metadata */}
                      <CardContent className="p-4 flex flex-col gap-3">
                        {/* Chemical Metadata Ribbon */}
                        <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-2">
                          <span className="text-2xs font-mono font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-xs">
                            {product.purity}
                          </span>
                          <span className="text-2xs font-mono text-muted-foreground truncate">
                            CAS: {product.cas}
                          </span>
                        </div>

                        {/* Title and Sequence */}
                        <div>
                          <h3 className="text-sm font-semibold text-foreground leading-snug line-clamp-2 hover:text-primary transition-colors">
                            <Link to="/ShopPage">
                              {product.name}
                            </Link>
                          </h3>
                          <p className="text-2xs font-mono text-muted-foreground/80 line-clamp-1 mt-1">
                            {product.sequence}
                          </p>
                        </div>
                      </CardContent>
                    </div>

                    {/* Pricing and Action Footer */}
                    <div className="p-4 pt-0 border-t border-border/40 mt-auto flex items-center justify-between gap-3">
                      <div>
                        <span className="text-2xs text-muted-foreground block">Research tier</span>
                        <span className="text-base font-bold text-foreground font-mono">
                          {product.formattedPrice}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleAddToCart}
                        className="text-xs h-8 px-3 gap-1.5"
                      >
                        <ShoppingCart className="size-3.5" />
                        <span>Order sample</span>
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {filteredProducts.length > 0 && (
              <div className="pt-6 border-t border-border mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-xs text-muted-foreground font-mono">
                  Showing 1-{filteredProducts.length} of {filteredProducts.length} catalogued lots
                </div>
                <Pagination className="mx-0 w-auto">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious href="#" className="text-xs pointer-events-none opacity-50" />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink href="#" isActive className="text-xs">
                        1
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext href="#" className="text-xs pointer-events-none opacity-50" />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}

            {/* Bulk / Institutional Order Callout */}
            <div className="border border-border bg-card rounded-md p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex flex-col gap-1">
                <h4 className="text-sm font-semibold text-foreground">
                  Custom milligram-to-gram peptide synthesis
                </h4>
                <p className="text-xs text-muted-foreground max-w-xl">
                  Supplying academic laboratories, pharmaceutical researchers, and government institutions in India with tailored sequences and analytical batch paperwork.
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="whitespace-nowrap text-xs">
                <Link to="/ContactPage">
                  Request institutional quote
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}